import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import { edpm, vae, assurancesMobilites, partageRoute } from '../data/mobilitesUrbaines';
import AudienceContextBanner from '../components/AudienceContextBanner';

const VehiculeTabs = ({ active, setActive }) => (
  <div className="mc-card p-2 flex gap-1 overflow-x-auto" data-testid="mobilites-tabs">
    {[
      { id: 'edpm', label: 'EDPM', sub: 'Trottinette · Monoroue · Gyropode', color: '#f59e0b' },
      { id: 'vae', label: 'VAE', sub: 'Vélo à assistance électrique', color: '#18794e' },
      { id: 'partage', label: 'Partage de la route', sub: 'Bonnes pratiques communes', color: '#cf2031' },
    ].map(t => {
      const isActive = active === t.id;
      return (
        <button
          key={t.id}
          onClick={() => setActive(t.id)}
          data-testid={`mobilites-tab-${t.id}`}
          className={cn(
            'flex-shrink-0 flex flex-col items-start text-left px-4 py-2.5 rounded-xl font-bold text-sm transition-all',
            isActive ? 'text-foreground shadow-md' : 'text-muted-foreground hover:bg-[hsl(var(--panel))]'
          )}
          style={isActive ? { background: t.color } : {}}
        >
          <span>{t.label}</span>
          <span className={cn('text-[10px] font-semibold mt-0.5', isActive ? 'text-foreground/80' : 'text-muted-foreground')}>
            {t.sub}
          </span>
        </button>
      );
    })}
  </div>
);

const VehiculeBlock = ({ data, assurance }) => {
  const Icon = Icons[data.icon] || Icons.Bike;

  return (
    <div className="space-y-4" data-testid={`mobilites-block-${data.id}`}>
      {/* Intro */}
      <div className="mc-card p-5 sm:p-6 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${data.color}22, transparent)` }}>
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full blur-3xl opacity-30" style={{ background: data.color }} />
        <div className="relative flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: data.color, color: 'white' }}>
            <Icon className="w-7 h-7" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: data.color }}>
              {data.tagline}
            </div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight">{data.label}</h2>
            <p className="text-sm text-foreground/80 mt-2 leading-relaxed">{data.description}</p>
          </div>
        </div>
      </div>

      {/* Réglementation */}
      <div className="mc-card p-5">
        <div className="text-[10px] font-black uppercase tracking-widest mb-3 flex items-center gap-1.5" style={{ color: data.color }}>
          <Icons.Scale className="w-3 h-3" />
          <span>Réglementation officielle</span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.reglementation.map((r, i) => (
            <div key={i} className="rounded-xl border border-border bg-[hsl(var(--panel))] p-3">
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">{r.label}</div>
              <div className="font-black text-lg mt-0.5" style={{ color: data.color }}>{r.value}</div>
              <div className="text-xs text-muted-foreground mt-1 leading-snug">{r.detail}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Équipement */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="mc-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Icons.AlertCircle className="w-4 h-4 text-[#b42318]" />
            <span className="text-[10px] font-black uppercase tracking-widest text-[#b42318]">Équipement obligatoire</span>
          </div>
          <ul className="space-y-2">
            {data.equipement.filter(e => e.obligatoire).map((e, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <Icons.CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#b42318]" />
                <div>
                  <div className="font-bold text-sm">{e.label}</div>
                  <div className="text-xs text-muted-foreground">{e.detail}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="mc-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Icons.Star className="w-4 h-4 text-[#18794e]" />
            <span className="text-[10px] font-black uppercase tracking-widest text-[#18794e]">Équipement recommandé</span>
          </div>
          <ul className="space-y-2">
            {data.equipement.filter(e => !e.obligatoire).map((e, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <Icons.Shield className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#18794e]" />
                <div>
                  <div className="font-bold text-sm">{e.label}</div>
                  <div className="text-xs text-muted-foreground">{e.detail}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Risques + Bonnes pratiques */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="mc-card p-5 bg-rose-50 border-rose-200 dark:bg-rose-950/40 dark:border-rose-900/50">
          <div className="text-[10px] font-black uppercase tracking-widest text-[#b42318] dark:text-rose-300 mb-3 flex items-center gap-1.5">
            <Icons.AlertTriangle className="w-3 h-3" />
            <span>Risques spécifiques</span>
          </div>
          <ul className="space-y-2">
            {data.risques.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <Icons.Flame className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#b42318] dark:text-rose-300" />
                <span className="text-foreground/90">{r}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="mc-card p-5 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-900/50">
          <div className="text-[10px] font-black uppercase tracking-widest text-[#18794e] dark:text-emerald-300 mb-3 flex items-center gap-1.5">
            <Icons.CheckCircle2 className="w-3 h-3" />
            <span>Bonnes pratiques</span>
          </div>
          <ul className="space-y-2">
            {data.bonnesPratiques.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <Icons.Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#18794e] dark:text-emerald-300" />
                <span className="text-foreground/90">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Assurance */}
      {assurance && (
        <div className="mc-card p-5 border-2 border-[#1d4ed8]/20 bg-[#eef5ff] dark:bg-blue-950/40 dark:border-blue-900/50" data-testid={`mobilites-assurance-${data.id}`}>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-9 h-9 rounded-xl bg-[#1d4ed8] text-white flex items-center justify-center">
              <Icons.ShieldCheck className="w-5 h-5" />
            </span>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-[#1d4ed8] dark:text-blue-300">Assurance {data.id.toUpperCase()}</div>
              <div className="font-black text-sm">Ce qui est obligatoire et conseillé.</div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="rounded-xl bg-white dark:bg-white/5 border border-[#1d4ed8]/20 dark:border-white/10 p-3">
              <div className="text-[10px] font-black uppercase tracking-widest text-[#b42318] dark:text-rose-300 mb-1.5">Obligatoire</div>
              <ul className="space-y-1.5">
                {assurance.obligatoire.map((o, i) => (
                  <li key={i} className="text-xs text-foreground/90 flex items-start gap-1.5">
                    <Icons.Check className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-[#b42318] dark:text-rose-300" />
                    <span>{o}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl bg-white dark:bg-white/5 border border-[#1d4ed8]/20 dark:border-white/10 p-3">
              <div className="text-[10px] font-black uppercase tracking-widest text-[#18794e] dark:text-emerald-300 mb-1.5">Conseillé</div>
              <ul className="space-y-1.5">
                {assurance.conseillee.map((c, i) => (
                  <li key={i} className="text-xs text-foreground/90 flex items-start gap-1.5">
                    <Icons.Check className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-[#18794e] dark:text-emerald-300" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <div className="flex items-center gap-2.5 rounded-xl bg-white dark:bg-white/5 border border-[#1d4ed8]/20 dark:border-white/10 p-3">
              <Icons.Euro className="w-4 h-4 flex-shrink-0 text-[#1d4ed8] dark:text-blue-300" />
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-[#1d4ed8] dark:text-blue-300">Coût annuel</div>
                <div className="text-xs font-bold">{assurance.coutAnnuel}</div>
              </div>
            </div>
            <div className="flex items-start gap-2 rounded-xl bg-[#fff7e8] border border-[#f5dfad] dark:bg-amber-950/40 dark:border-amber-900/50 p-3">
              <Icons.Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#9a6700] dark:text-amber-300" />
              <div className="text-xs text-foreground/90">{assurance.note}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PartageRouteBlock = () => (
  <div className="space-y-4" data-testid="mobilites-partage-block">
    <div className="mc-card p-6 sm:p-8 relative overflow-hidden text-foreground">
      <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full blur-3xl opacity-40" style={{ background: '#cf2031' }} />
      <div className="relative">
        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1.5 text-[10px] font-black text-foreground uppercase tracking-widest mb-3">
          <Icons.Users className="w-3.5 h-3.5" /> Partage de la route
        </div>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight">
          La route, c'est tout le monde.<br />
          <span className="text-[#ff7a84]">Ta sécurité aussi.</span>
        </h2>
        <p className="text-sm text-foreground/80 mt-3 max-w-xl">
          6 principes qui valent pour TOUS les usagers vulnérables : motards, motoristes, cyclistes, utilisateurs d'EDPM et piétons.
        </p>
      </div>
    </div>

    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {partageRoute.map((p, i) => {
        const PIcon = Icons[p.icon] || Icons.Circle;
        return (
          <div key={i} className="mc-card p-5 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-200" data-testid={`partage-card-${i}`}>
            <div className="w-11 h-11 rounded-2xl mb-3 flex items-center justify-center bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]">
              <PIcon className="w-5 h-5" />
            </div>
            <h4 className="font-black text-lg mb-1.5">{p.title}</h4>
            <p className="text-sm text-foreground/80 leading-relaxed mb-3">{p.desc}</p>
            <div className="flex items-start gap-2 rounded-xl bg-[hsl(var(--primary-soft))] p-2.5">
              <Icons.Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5 text-[hsl(var(--primary))]" />
              <span className="text-xs font-semibold text-[hsl(var(--primary-dark))]">{p.tip}</span>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

export default function MobilitesUrbaines() {
  const navigate = useNavigate();
  const [active, setActive] = useState('edpm');

  return (
    <div className="space-y-5" data-testid="mobilites-page">
      {/* Hero */}
      <section className="mc-card relative overflow-hidden" data-testid="mobilites-hero">
        <div className="absolute -top-16 -right-16 w-60 h-60 rounded-full blur-3xl opacity-30" style={{ background: '#f59e0b' }} />
        <div className="absolute -bottom-16 -left-16 w-60 h-60 rounded-full blur-3xl opacity-20" style={{ background: '#18794e' }} />
        <div className="relative p-6 sm:p-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1.5 text-[10px] font-black text-foreground uppercase tracking-widest mb-4">
            <Icons.Bike className="w-3 h-3" />
            <span>Mobilités urbaines</span>
            <Icons.Zap className="w-3 h-3" />
          </div>
          <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-foreground leading-[1.05]">
            Trottinettes, vélos électriques,<br />
            <span className="text-[#ff7a84]">usagers vulnérables comme toi.</span>
          </h1>
          <p className="text-sm sm:text-base text-foreground/80 mt-4 max-w-xl">
            Parce qu'apprendre la route, c'est aussi connaître les règles, les équipements et les assurances qui s'appliquent à tous les conducteurs de 2 & 3 roues.
          </p>
          <div className="flex gap-2 mt-5 flex-wrap">
            <Button onClick={() => setActive('edpm')} className="c2r-touch-feedback h-11 px-5 text-sm rounded-full bg-[#f59e0b] text-white hover:bg-[#f59e0b]/90 font-black" data-testid="hero-cta-edpm">
              <Icons.Zap className="w-4 h-4 mr-1.5" /> EDPM
            </Button>
            <Button onClick={() => setActive('vae')} className="c2r-touch-feedback h-11 px-5 text-sm rounded-full bg-[#18794e] text-white hover:bg-[#18794e]/90 font-black" data-testid="hero-cta-vae">
              <Icons.Bike className="w-4 h-4 mr-1.5" /> VAE
            </Button>
            <Button onClick={() => setActive('partage')} variant="outline" className="h-11 px-5 text-sm rounded-full bg-transparent border-white/30 text-foreground hover:bg-white/10 hover:text-foreground" data-testid="hero-cta-partage">
              Partage de la route
            </Button>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <VehiculeTabs active={active} setActive={setActive} />

      {/* Bandeau d'adaptation par audience (visible pour les 3 publics) */}
      <AudienceContextBanner
        pageHint="Les mobilités urbaines (EDPM, VAE) concernent tout le monde : apprendre à cohabiter, enseigner la conscientisation, citer la réglementation à l\'oral."
        hideOnEmpty
        contexts={{
          eleve: {
            title: 'Cohabiter avec les autres usagers de la route',
            bullets: [
              'Trottinette, vélo électrique : règles, équipements obligatoires, assurance.',
              'Anticipe les comportements à risque (trottoirs, voies réservées, casque).',
            ],
          },
          enseignant: {
            title: 'Intégrer la conscientisation aux autres usagers en cours',
            bullets: [
              'Utilise ces fiches dans tes modules sur les usagers vulnérables.',
              'Croise avec la matrice GDE niveau 4 (contexte sociétal).',
            ],
            cta: 'Aller au Grand Quiz C2',
            ctaTo: '/enseignant/grand-quiz-c2',
          },
          'futur-enseignant': {
            title: 'Réglementation EDPM/VAE attendue à l\'oral CCS',
            bullets: [
              'Cite l\'âge minimum (12 ans EDPM), la limite à 25 km/h, les voies autorisées.',
              'Sache croiser avec la sécurité routière (ONISR) et la responsabilité civile.',
            ],
            cta: 'Voir la banque de questions jury',
            ctaTo: '/enseignant/banque-questions-jury',
          },
        }}
      />

      {/* Contenu actif */}
      {active === 'edpm' && <VehiculeBlock data={edpm} assurance={assurancesMobilites.edpm} />}
      {active === 'vae' && <VehiculeBlock data={vae} assurance={assurancesMobilites.vae} />}
      {active === 'partage' && <PartageRouteBlock />}

      {/* CTA bas : comparaison avec permis moto */}
      <section className="mc-card p-6 text-center" data-testid="mobilites-cta-bottom">
        <h3 className="text-xl font-black tracking-tight mb-2">Besoin d'aller plus loin ?</h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-lg mx-auto">
          Si tu vises un vrai permis 2 roues (AM, A1, A2, Passerelle, 125 ou L5e), retrouve tous les parcours de formation avec pré-requis et modules pédagogiques.
        </p>
        <Button onClick={() => navigate('/formation')} className="mc-btn-primary" data-testid="mobilites-cta-formation">
          Voir les parcours de formation <Icons.ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </section>
    </div>
  );
}
